import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { Button } from '../ui/Button'
import EnterpriseOperationalSection from '../enterprise-operational/EnterpriseOperationalSection'
import { ComplianceCenterNavTabs } from '../compliance-center/ComplianceCenterNavTabs'
import GcpSourcesPanel from './GcpSourcesPanel'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'
import { getActiveOrgId, listUserOrganizations } from '../../utils/orgUtils'
import { featureFlags } from '../../config/featureFlags'
import {
  acceptScopedResponse,
  appendUniqueResources,
  buildCloudRequestUrl,
  cloudFailureCode,
} from './gcpDiscoverySession.mjs'
import { buildResourceListPath } from '../../pages/integrations/integrationsRoutes.mjs'

type Props = { token: string }

type Capability = {
  capability: string
  status: string
  count: number
  warningCodes?: string[]
  reason?: string | null
}

type Integration = {
  id: string
  projectId: string
  displayName?: string | null
  lifecycleStatus: string
  authMode: string
  credentialPresent: boolean
  credentialVersion: number
  clientEmailMasked?: string | null
  lastTestResult?: { capabilities?: Capability[] } | null
  settings?: { forbidPublicVmAddress?: boolean }
}

type SyncRun = {
  id: string
  status: string
  errorCode?: string | null
  createdAt?: string
  startedAt?: string | null
  finishedAt?: string | null
  summary?: { resourceCount?: number } | null
  capabilityResults?: Capability[]
}

type CloudResource = {
  id: string
  kind: string
  name?: string | null
  location?: string | null
  lastSeenAt?: string
  assetId?: string | null
  normalized?: unknown
}

type ResourceRelation = {
  type: string
  resourceId: string
  kind: string
  name?: string | null
  observedTraffic: boolean
}

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

async function cloudRequest(token: string, orgId: string, path: string, init?: RequestInit) {
  const response = await fetch(buildCloudRequestUrl(API_BASE_URL, path, orgId), {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(cloudFailureCode(response.status, data))
  return data
}

function resourceSummary(value: unknown, prefix = ''): Array<{ key: string; value: string | null }> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return []
  const rows: Array<{ key: string; value: string | null }> = []
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    const label = prefix ? `${prefix}.${key}` : key
    if (entry == null || ['string', 'number', 'boolean'].includes(typeof entry)) {
      rows.push({ key: label, value: String(entry ?? '') })
    } else if (!prefix && entry && typeof entry === 'object' && !Array.isArray(entry)) {
      rows.push(...resourceSummary(entry, label))
    } else {
      rows.push({ key: label, value: null })
    }
  }
  return rows
}

function runDuration(run: SyncRun) {
  if (!run.startedAt || !run.finishedAt) return ''
  const ms = new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime()
  if (!Number.isFinite(ms) || ms < 0) return ''
  return `${Math.round(ms / 1000)}s`
}

export default function GcpDiscoveryPanel({ token }: Props) {
  const { t } = useI18n()
  const params = useParams()
  const section = params.section === 'inventory' || params.section === 'history' || params.section === 'sources' ? params.section : 'connection'
  const [orgId, setOrgId] = useState<string | null>(() => getActiveOrgId())
  const [integrations, setIntegrations] = useState<Integration[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [runs, setRuns] = useState<SyncRun[]>([])
  const [resources, setResources] = useState<CloudResource[]>([])
  const [projectId, setProjectId] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [serviceAccount, setServiceAccount] = useState('')
  const [replacement, setReplacement] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [linkAssetId, setLinkAssetId] = useState('')
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [configId, setConfigId] = useState<string | null>(null)
  const [kind, setKind] = useState('INSTANCE')
  const [registeredAssetId, setRegisteredAssetId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [kindCounts, setKindCounts] = useState<Array<{ kind: string; count: number }>>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [credentialOpen, setCredentialOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [accessDenied, setAccessDenied] = useState(false)
  const [relations, setRelations] = useState<ResourceRelation[]>([])
  const [checks, setChecks] = useState<Array<{ ruleId: string; status: string; reason?: string | null }>>([])
  const viewRef = useRef({ generation: 0, orgId: null as string | null, selectedId: null as string | null })

  const clearDetails = () => {
    setRuns([])
    setResources([])
    setConfigId(null)
    setReplacement('')
    setKind('INSTANCE')
    setSearch('')
    setAppliedSearch('')
    setKindCounts([])
    setNextCursor(null)
    setCredentialOpen(false)
    setCreateOpen(false)
    setServiceAccount('')
    setRelations([])
    setChecks([])
  }

  const clearOrgView = () => {
    setIntegrations([])
    setSelectedId(null)
    setProjectId('')
    setDisplayName('')
    setServiceAccount('')
    setMessage(null)
    setAccessDenied(false)
    clearDetails()
  }

  useEffect(() => {
    const applyOrg = (next: string | null) => {
      setOrgId((prev) => (prev === next ? prev : next))
    }
    const onOrg = (event: Event) => {
      const detail = (event as CustomEvent<{ organizationId?: string | null }>).detail
      const next = detail && 'organizationId' in detail ? detail.organizationId || null : getActiveOrgId()
      applyOrg(next)
    }
    const onStorage = () => applyOrg(getActiveOrgId())
    window.addEventListener('ds-active-organization-changed', onOrg)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener('ds-active-organization-changed', onOrg)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  useEffect(() => {
    viewRef.current = {
      generation: viewRef.current.generation + 1,
      orgId,
      selectedId: null,
    }
    clearOrgView()
  }, [orgId, token])

  const load = useCallback(async () => {
    if (!orgId || !token) return
    const ticket = { ...viewRef.current }
    setLoading(true)
    try {
      const data = await cloudRequest(token, ticket.orgId || orgId, '')
      if (!acceptScopedResponse(ticket, viewRef.current)) return
      setAccessDenied(false)
      setIntegrations(Array.isArray(data.integrations) ? data.integrations : [])
    } catch (error) {
      if (!acceptScopedResponse(ticket, viewRef.current)) return
      const code = error instanceof Error ? error.message : 'CLOUD_ERROR'
      if (code === 'ORGANIZATION_PERMISSION_DENIED') {
        setAccessDenied(true)
        setIntegrations([])
        setMessage(null)
      } else {
        setMessage(code)
      }
    } finally {
      if (acceptScopedResponse(ticket, viewRef.current)) setLoading(false)
    }
  }, [orgId, token])

  useEffect(() => {
    void load()
  }, [load])

  const selectIntegration = (id: string) => {
    if (!orgId) return
    viewRef.current = { generation: viewRef.current.generation + 1, orgId, selectedId: id }
    setSelectedId(id)
    clearDetails()
  }

  const loadSection = useCallback(async (append = false, cursor = '') => {
    const ticket = { ...viewRef.current }
    const id = ticket.selectedId
    if (!ticket.orgId || !id || !token) return
    try {
      if (section === 'history') {
        const data = await cloudRequest(token, ticket.orgId, `/${id}/runs`)
        if (!acceptScopedResponse(ticket, viewRef.current)) return
        setRuns(Array.isArray(data.runs) ? data.runs : [])
        return
      }
      if (section !== 'inventory') return
      const data = await cloudRequest(
        token,
        ticket.orgId,
        buildResourceListPath(id, { kind, search: appliedSearch, cursor }),
      )
      if (!acceptScopedResponse(ticket, viewRef.current)) return
      const items = Array.isArray(data.items) ? data.items : []
      setResources((current) => (append ? appendUniqueResources(current, items) : items))
      setNextCursor(typeof data.nextCursor === 'string' ? data.nextCursor : null)
      setKindCounts(Array.isArray(data.kindCounts) ? data.kindCounts : [])
    } catch (error) {
      if (!acceptScopedResponse(ticket, viewRef.current)) return
      setMessage(error instanceof Error ? error.message : 'CLOUD_ERROR')
    }
  }, [appliedSearch, kind, section, token])

  useEffect(() => {
    void loadSection(false, '')
  }, [loadSection, selectedId])

  const createConnection = async () => {
    if (!orgId || busy) return
    const body = serviceAccount
    setServiceAccount('')
    let parsed
    try {
      parsed = JSON.parse(body)
    } catch {
      setMessage('VALIDATION')
      return
    }
    const ticket = { ...viewRef.current }
    setBusy(true)
    try {
      await cloudRequest(token, ticket.orgId || orgId, '', {
        method: 'POST',
        body: JSON.stringify({ projectId, displayName, serviceAccount: parsed }),
      })
      if (!acceptScopedResponse(ticket, viewRef.current)) return
      setProjectId('')
      setDisplayName('')
      setCreateOpen(false)
      setMessage(null)
      await load()
    } catch (error) {
      if (!acceptScopedResponse(ticket, viewRef.current)) return
      setMessage(error instanceof Error ? error.message : 'CLOUD_ERROR')
    } finally {
      setBusy(false)
    }
  }

  const mutate = async (path: string, body?: unknown) => {
    if (!orgId || !selectedId || busy) return
    const ticket = { ...viewRef.current, selectedId }
    if (path.endsWith('/credentials')) setReplacement('')
    setBusy(true)
    try {
      await cloudRequest(token, ticket.orgId || orgId, `/${selectedId}${path}`, {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      })
      if (!acceptScopedResponse(ticket, viewRef.current)) return
      setMessage(null)
      await load()
      if (!acceptScopedResponse(ticket, viewRef.current)) return
      selectIntegration(selectedId)
    } catch (error) {
      if (!acceptScopedResponse(ticket, viewRef.current)) return
      setMessage(error instanceof Error ? error.message : 'CLOUD_ERROR')
    } finally {
      setBusy(false)
    }
  }

  const selected = integrations.find((item) => item.id === selectedId) || null
  const capabilities = selected?.lastTestResult?.capabilities || []
  const visibleConfig = resources.find((item) => item.id === configId)
  const organizationName = listUserOrganizations().find((item) => item.id === orgId)?.name || orgId || ''
  const closeCredentials = () => {
    setServiceAccount('')
    setReplacement('')
    setCredentialOpen(false)
    setCreateOpen(false)
  }
  const closeResource = () => {
    setConfigId(null)
    setRelations([])
    setChecks([])
  }

  const openResource = (resource: CloudResource) => {
    setConfigId(resource.id)
    setRelations([])
    setChecks([])
    if (!orgId || !selectedId) return
    const ticket = { ...viewRef.current }
    void cloudRequest(token, ticket.orgId || orgId, `/${selectedId}/resources/${resource.id}/relations`).then((body) => {
      if (!acceptScopedResponse(ticket, viewRef.current)) return
      setRelations(Array.isArray(body.relations) ? body.relations : [])
      setChecks(Array.isArray(body.checks) ? body.checks : [])
    }).catch(() => {
      if (!acceptScopedResponse(ticket, viewRef.current)) return
      setRelations([])
      setChecks([])
    })
  }

  const registerResource = (resourceId: string) => {
    if (!orgId || !selectedId || busy) return
    const ticket = { ...viewRef.current }
    setBusy(true)
    void cloudRequest(token, ticket.orgId || orgId, `/${selectedId}/resources/${resourceId}/register`, { method: 'POST' }).then((body) => {
      if (!acceptScopedResponse(ticket, viewRef.current)) return
      const assetId = typeof body.assetId === 'string' ? body.assetId : null
      setRegisteredAssetId(assetId)
      setResources((current) => current.map((row) => (row.id === resourceId ? { ...row, assetId } : row)))
    }).catch((error) => {
      if (!acceptScopedResponse(ticket, viewRef.current)) return
      setMessage(error instanceof Error ? error.message : 'CLOUD_ERROR')
    }).finally(() => {
      if (acceptScopedResponse(ticket, viewRef.current)) setBusy(false)
    })
  }

  useEffect(() => {
    if (!createOpen && !credentialOpen && !configId) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      closeCredentials()
      closeResource()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [createOpen, credentialOpen, configId])

  return (
    <EnterpriseOperationalSection
      layerLabel={t('gcpIntegration.title', 'Google Cloud')}
      title={t('gcpIntegration.title', 'Google Cloud')}
      subtitle={organizationName
        ? t('integrations.overview.org', 'Organization: {name}', { name: organizationName })
        : t('gcpIntegration.noOrg', 'Select an organization before managing Google Cloud.')}
      testId="integrations-google-cloud"
    >
      <ComplianceCenterNavTabs
        variant={featureFlags.ENTERPRISE_OPERATIONAL_UX_V1 ? 'enterprise' : 'default'}
        ariaLabel={t('integrations.nav.googleCloud', 'Google Cloud')}
        items={[
          { key: 'connection', to: '/integrations/google-cloud/connection', label: t('gcpIntegration.connection', 'Connection') },
          { key: 'inventory', to: '/integrations/google-cloud/inventory', label: t('gcpIntegration.resources', 'Inventory') },
          { key: 'history', to: '/integrations/google-cloud/history', label: t('gcpIntegration.runs', 'Sync history') },
          { key: 'sources', to: '/integrations/google-cloud/sources', label: t('gcpSources.title', 'Sources') },
        ]}
      />
      <p className={entStyles.metaRow}>{t('gcpIntegration.disclaimer', 'A completed inventory is not a compliance result, drift finding, or security score.')}</p>
      {accessDenied && (
        <p className={entStyles.metaRow} data-testid="gcp-permission-denied">
          {t('integrations.overview.permission', 'You do not have permission to view one or more integration sources.')}
        </p>
      )}
      {!orgId && <p className={entStyles.metaRow}>{t('gcpIntegration.noOrg', 'Select an organization before managing Google Cloud.')}</p>}
      {message && <p className={entStyles.metaRow}>{message}</p>}
      {loading && <p className={entStyles.metaRow}>{t('common.loading', 'Loading…')}</p>}
      {section === 'connection' && (
        <Button type="button" size="small" color="primary" disabled={busy || !orgId || accessDenied} onClick={() => setCreateOpen(true)}>
          {t('gcpIntegration.create', 'Create connection')}
        </Button>
      )}
      {createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={t('gcpIntegration.credentials', 'Service account JSON')}>
          <button type="button" className="absolute inset-0 bg-black/30" aria-label={t('common.cancel', 'Cancel')} onClick={closeCredentials} />
          <div className={`${entStyles.formActions} relative bg-white p-4 max-w-lg w-full`}>
            <label>
              {t('gcpIntegration.projectId', 'GCP project ID')}
              <input value={projectId} onChange={(event) => setProjectId(event.target.value)} autoComplete="off" />
            </label>
            <label>
              {t('gcpIntegration.name', 'Name')}
              <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} autoComplete="off" />
            </label>
            <label>
              {t('gcpIntegration.credentials', 'Service account JSON')}
              <textarea value={serviceAccount} onChange={(event) => setServiceAccount(event.target.value)} autoComplete="off" spellCheck={false} />
            </label>
            <Button type="button" size="small" color="primary" disabled={busy || !orgId} onClick={() => void createConnection()}>
              {t('gcpIntegration.create', 'Create connection')}
            </Button>
            <Button type="button" size="small" color="outline" onClick={closeCredentials}>
              {t('common.cancel', 'Cancel')}
            </Button>
          </div>
        </div>
      )}
      {orgId && !accessDenied && integrations.length === 0 && <p className={entStyles.metaRow}>{t('gcpIntegration.empty', 'No Google Cloud connections for this organization.')}</p>}

      {orgId && !accessDenied && <div className={entStyles.tableWrap}>
        <table className={entStyles.dataTable}>
          <thead>
            <tr>
              <th>{t('gcpIntegration.projectId', 'GCP project ID')}</th>
              <th>{t('gcpIntegration.lifecycle', 'Lifecycle')}</th>
              <th>{t('gcpIntegration.authMode', 'Auth mode')}</th>
              <th>{t('gcpIntegration.status', 'Status')}</th>
            </tr>
          </thead>
          <tbody>
            {integrations.map((item) => (
              <tr key={item.id} data-testid={`gcp-integration-${item.projectId}`}>
                <td>{item.projectId}</td>
                <td>{item.lifecycleStatus}</td>
                <td>{item.authMode}</td>
                <td>
                  <Button type="button" size="small" color="outline" onClick={() => void selectIntegration(item.id)}>
                    {item.clientEmailMasked || item.displayName || item.projectId}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>}

      {selected && section === 'connection' && (
        <div data-testid="gcp-integration-detail">
          <p className={entStyles.metaRow}>
            {selected.projectId} · {selected.lifecycleStatus} · {selected.authMode}
            {selected.credentialPresent
              ? ` · ${t('gcpIntegration.credentialPresent', 'Credentials stored')}`
              : ` · ${t('gcpIntegration.credentialMissing', 'Credentials missing')}`}
          </p>
          <div className={entStyles.formActions}>
            <Button type="button" size="small" color="outline" disabled={busy} onClick={() => void mutate('/test')}>{t('gcpIntegration.test', 'Test connection')}</Button>
            <Button type="button" size="small" color="primary" disabled={busy} onClick={() => void mutate('/sync')}>{t('gcpIntegration.sync', 'Run sync')}</Button>
            <Button type="button" size="small" color="outline" disabled={busy} onClick={() => void load()}>{t('gcpIntegration.refresh', 'Refresh')}</Button>
            <Button type="button" size="small" color="outline" disabled={busy} onClick={() => void mutate('/disable')}>{t('gcpIntegration.disable', 'Disable')}</Button>
            <Button type="button" size="small" color="outline" disabled={busy} onClick={() => void mutate('/enable')}>{t('gcpIntegration.enable', 'Enable')}</Button>
            <Button type="button" size="small" color="danger" disabled={busy} onClick={() => void mutate('/revoke')}>{t('gcpIntegration.revoke', 'Revoke')}</Button>
            <Button type="button" size="small" color="outline" disabled={busy} onClick={() => setCredentialOpen(true)}>{t('gcpIntegration.replaceCredentials', 'Replace credentials')}</Button>
          </div>
          <label className={entStyles.checkboxRow}>
            <input
              type="checkbox"
              checked={selected.settings?.forbidPublicVmAddress === true}
              onChange={(event) => {
                void cloudRequest(token, orgId || '', `/${selected.id}/settings`, {
                  method: 'PATCH',
                  body: JSON.stringify({ forbidPublicVmAddress: event.target.checked }),
                }).then(() => load())
              }}
            />
            {t('gcpIntegration.forbidPublicAddress', 'Treat a public VM address as a finding for this connection')}
          </label>
          {credentialOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={t('gcpIntegration.replaceCredentials', 'Replace credentials')}>
              <button type="button" className="absolute inset-0 bg-black/30" aria-label={t('common.cancel', 'Cancel')} onClick={closeCredentials} />
              <div className="relative bg-white p-4 max-w-lg w-full">
              <label>
                {t('gcpIntegration.replaceCredentials', 'Replace credentials')}
                <textarea value={replacement} onChange={(event) => setReplacement(event.target.value)} autoComplete="off" spellCheck={false} />
              </label>
              <Button
                type="button"
                size="small"
                color="outline"
                disabled={busy}
                onClick={() => {
                  if (busy) return
                  const body = replacement
                  setReplacement('')
                  try {
                    void mutate('/credentials', { serviceAccount: JSON.parse(body) })
                    closeCredentials()
                  } catch {
                    setMessage('VALIDATION')
                  }
                }}
              >
                {t('gcpIntegration.replaceCredentials', 'Replace credentials')}
              </Button>
              <Button type="button" size="small" color="outline" onClick={closeCredentials}>{t('common.cancel', 'Cancel')}</Button>
              </div>
            </div>
          )}
          <h3>{t('gcpIntegration.capabilities', 'Capabilities')}</h3>
          {capabilities.length === 0 ? (
            <p className={entStyles.metaRow}>{t('integrations.overview.notChecked', 'Not checked')}</p>
          ) : (
            <ul>
              {capabilities.map((item) => (
                <li key={item.capability}>
                  {item.capability}: {item.status}
                  {item.reason ? ` (${item.reason})` : ''}
                  {item.warningCodes?.length ? ` [${item.warningCodes.join(', ')}]` : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {selected && section === 'inventory' && (
        <div data-testid="gcp-inventory">
          <div className={entStyles.formActions}>
            {kindCounts.map((item) => (
              <Button key={item.kind} type="button" size="small" color={kind === item.kind ? 'primary' : 'outline'} onClick={() => { setKind(item.kind); setResources([]); setNextCursor(null) }}>
                {item.kind}: {item.count}
              </Button>
            ))}
            <Button type="button" size="small" color={kind === 'INSTANCE' ? 'primary' : 'outline'} onClick={() => { setKind('INSTANCE'); setResources([]); setNextCursor(null) }}>
              {t('gcpIntegration.virtualMachines', 'Virtual machines')}
            </Button>
            <Button type="button" size="small" color={kind === '' ? 'primary' : 'outline'} onClick={() => { setKind(''); setResources([]); setNextCursor(null) }}>
              {t('gcpIntegration.allResources', 'All resources')}
            </Button>
            <label>
              {t('common.search', 'Search')}
              <input value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  setResources([])
                  setNextCursor(null)
                  setAppliedSearch(search.trim())
                }
              }} />
            </label>
            <Button type="button" size="small" color="outline" onClick={() => { setResources([]); setNextCursor(null); setAppliedSearch(search.trim()) }}>
              {t('common.search', 'Search')}
            </Button>
            <Button type="button" size="small" color="outline" disabled={busy} onClick={() => { setResources([]); setNextCursor(null); void loadSection(false, '') }}>
              {t('gcpIntegration.refresh', 'Refresh')}
            </Button>
          </div>
          <div className={entStyles.tableWrap}>
            <table className={entStyles.dataTable}>
              <thead>
                <tr>
                  <th>{t('gcpIntegration.name', 'Name')}</th>
                  <th>{t('gcpIntegration.kind', 'Kind')}</th>
                  <th>{t('gcpIntegration.location', 'Location')}</th>
                  <th>{t('gcpIntegration.lastSeen', 'Last seen')}</th>
                  <th>{t('gcpIntegration.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {resources.map((resource) => (
                  <tr key={resource.id}>
                    <td>
                      <button type="button" onClick={() => openResource(resource)}>{resource.name}</button>
                    </td>
                    <td>{resource.kind}</td>
                    <td>{resource.location}</td>
                    <td>{resource.lastSeenAt || ''}</td>
                    <td>
                      {resource.kind === 'INSTANCE' && resource.assetId ? (
                        <Link to={`/assets/${resource.assetId}`}>{t('gcpIntegration.openAsset', 'Open asset')}</Link>
                      ) : null}
                      {resource.kind === 'INSTANCE' && !resource.assetId ? (
                        <>
                          <Button type="button" size="small" color="outline" disabled={busy} onClick={() => {
                            void cloudRequest(token, orgId || '', `/${selectedId}/resources/${resource.id}/register-preview`).then((body) => {
                              setMessage(body.preview ? `${body.preview.name} · ${body.preview.automaticMatch ? 'matched' : 'no automatic match'}` : null)
                            })
                          }}>{t('gcpIntegration.previewRegistration', 'Preview registration')}</Button>
                          <Button type="button" size="small" color="outline" disabled={busy} onClick={() => registerResource(resource.id)}>
                            {t('gcpIntegration.registerAsset', 'Register as asset')}
                          </Button>
                          <input value={linkAssetId} onChange={(event) => setLinkAssetId(event.target.value)} placeholder={t('gcpIntegration.existingAsset', 'Existing asset id')} />
                          <Button type="button" size="small" color="outline" disabled={busy || !linkAssetId} onClick={() => {
                            void cloudRequest(token, orgId || '', `/${selectedId}/resources/${resource.id}/link`, {
                              method: 'POST',
                              body: JSON.stringify({ assetId: linkAssetId.trim() }),
                            }).then(() => { setLinkAssetId(''); void loadSection(false, '') })
                          }}>{t('gcpIntegration.linkAsset', 'Link existing asset')}</Button>
                        </>
                      ) : null}
                      {resource.kind === 'INSTANCE' && resource.assetId ? (
                        <Button type="button" size="small" color="outline" disabled={busy} onClick={() => {
                          void cloudRequest(token, orgId || '', `/${selectedId}/resources/${resource.id}/link`, { method: 'DELETE' }).then(() => loadSection(false, ''))
                        }}>{t('gcpIntegration.unlinkAsset', 'Unlink asset')}</Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {registeredAssetId ? <p><Link to={`/assets/${registeredAssetId}`}>{t('gcpIntegration.openAsset', 'Open asset')}</Link></p> : null}
          {nextCursor && (
            <Button type="button" size="small" color="outline" disabled={busy} onClick={() => void loadSection(true, nextCursor)}>
              {t('gcpIntegration.loadMore', 'Load more')}
            </Button>
          )}
          {visibleConfig && (
            <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label={visibleConfig.name || visibleConfig.kind} data-testid="gcp-resource-config">
              <button type="button" className="flex-1 bg-black/30" aria-label={t('common.close', 'Close')} onClick={closeResource} />
              <aside className="w-full max-w-md bg-white shadow-xl h-full overflow-y-auto p-4">
                <h3>{visibleConfig.name || visibleConfig.kind}</h3>
                <p className={entStyles.metaRow}>{visibleConfig.kind} · {visibleConfig.location || ''} · {visibleConfig.lastSeenAt || ''}</p>
                <h4>{t('gcpChecks.findings', 'Configuration findings')}</h4>
                {checks.length === 0 ? (
                  <p className={entStyles.metaRow}>{t('gcpChecks.none', 'No configuration findings for this resource.')}</p>
                ) : (
                  <ul>
                    {checks.map((check) => (
                      <li key={`${check.ruleId}:${check.reason || ''}`}>
                        <Link to="/compliance-center/automation">{check.ruleId}</Link>
                        {` · ${check.status}${check.reason ? ` · ${check.reason}` : ''}`}
                      </li>
                    ))}
                  </ul>
                )}
                <h4>{t('gcpIntegration.technicalLinks', 'Technical links')}</h4>
                {relations.length === 0 ? (
                  <p className={entStyles.metaRow}>{t('gcpIntegration.noTechnicalLinks', 'No collected technical links for this resource.')}</p>
                ) : (
                  <ul>
                    {relations.map((relation) => (
                      <li key={`${relation.type}:${relation.resourceId}`}>
                        {relation.type}: {relation.name || relation.kind}
                        {relation.type === 'CONFIGURED_ON_SAME_NETWORK'
                          ? ` · ${t('gcpIntegration.notObservedTraffic', 'Configured on the same network. This is not observed traffic.')}`
                          : ''}
                      </li>
                    ))}
                  </ul>
                )}
                <dl>
                  {resourceSummary(visibleConfig.normalized).filter((field) => field.value != null).map((field) => (
                    <div key={field.key}>
                      <dt>{field.key}</dt>
                      <dd>{field.value}</dd>
                    </div>
                  ))}
                </dl>
                {resourceSummary(visibleConfig.normalized).some((field) => field.value == null) ? (
                  <details>
                    <summary>{t('gcpIntegration.config', 'Configuration')}</summary>
                    <dl>
                      {resourceSummary(visibleConfig.normalized).filter((field) => field.value == null).map((field) => (
                        <div key={field.key}><dt>{field.key}</dt><dd>{t('gcpIntegration.nestedConfig', 'Additional configuration is available in this record.')}</dd></div>
                      ))}
                    </dl>
                  </details>
                ) : null}
                <Button type="button" size="small" color="outline" onClick={closeResource}>{t('common.close', 'Close')}</Button>
              </aside>
            </div>
          )}
        </div>
      )}
      {selected && section === 'sources' && orgId && (
        <GcpSourcesPanel token={token} orgId={orgId} integrationId={selected.id} />
      )}
      {selected && section === 'history' && (
        <div data-testid="gcp-sync-history">
          <Button type="button" size="small" color="outline" disabled={busy} onClick={() => void loadSection(false, '')}>
            {t('gcpIntegration.refresh', 'Refresh')}
          </Button>
          <ul>
            {runs.map((run) => (
              <li key={run.id}>
                {run.status}
                {run.startedAt ? ` · ${run.startedAt}` : ''}
                {run.finishedAt ? ` · ${run.finishedAt}` : ''}
                {runDuration(run) ? ` · ${runDuration(run)}` : ''}
                {typeof run.summary?.resourceCount === 'number' ? ` · ${run.summary.resourceCount}` : ''}
                {run.errorCode ? ` · ${run.errorCode}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </EnterpriseOperationalSection>
  )
}
